// App.tsx
import { useEffect, useState } from "react";
import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import useHeartbeat from "@/hooks/useHeartbeat";
import { getAccessToken, bootstrapAuth } from "@/lib/authToken";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import CookieBanner from "@/components/CookieBanner";
import CookieSettings from "@/components/CookieSettings";
import { CookieConsentProvider, useCookieConsent } from "@/hooks/useCookieConsent";
import { initConsentEffects } from "@/lib/consentEffects";

import NotFound from "@/pages/not-found";
import Landing from "@/pages/Landing";
import Home from "@/pages/Home";
import Dashboard from "@/pages/Dashboard";
import StartTrading from "@/pages/StartTrading";
import Profile from "@/pages/Profile";
import Pricing from "@/pages/Pricing";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfUse from "@/pages/TermsOfUse";
import RiskDisclosure from "@/pages/RiskDisclosure";
import RefundPolicy from "@/pages/RefundPolicy";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Contact from "@/pages/Contact";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import HowItWorks from "@/pages/HowItWorks";
import About from "@/pages/About";
import PaymentReturn from "@/pages/PaymentReturn";
import PaymentConfirming from "@/pages/PaymentConfirming";
import LoadingSpinner from "@/components/LoadingSpinner";
import ScrollToTop from "@/components/ScrollToTop";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const [, setLocation] = useLocation();

  const online = useOnlineStatus();
  const offline = !online;
  const hasToken = !!getAccessToken();

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !(offline && hasToken)) {
      setLocation("/login");
    }
  }, [isLoading, isAuthenticated, offline, hasToken, setLocation]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(226,26%,7%)]">
        <div className="text-center">
          <LoadingSpinner />
          <p className="text-[hsl(210,9%,91%)] mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (offline && hasToken) {

      return <>{children}</>;
    }
    return null;
  }
  return <>{children}</>;
}

function Router() {
  return (
    <Switch>
      
      <Route path="/login" component={Login} />
      <Route path="/register" component={Register} />
      <Route path="/pricing" component={Pricing} />
      <Route path="/payment/return" component={PaymentReturn} />
      <Route path="/payments/confirming">
        <ProtectedRoute>
          <PaymentConfirming />
        </ProtectedRoute>
      </Route>

  <Route path="/how-it-works" component={HowItWorks} />
  <Route path="/about" component={About} />
      <Route path="/privacy" component={PrivacyPolicy} />
      <Route path="/terms" component={TermsOfUse} />
      <Route path="/risk" component={RiskDisclosure} />
  <Route path="/refund" component={RefundPolicy} />
      <Route path="/contact" component={Contact} />
  <Route path="/forgot-password" component={ForgotPassword} />
  <Route path="/forgot-password/" component={ForgotPassword} />
  <Route path="/reset-password/:token" component={ResetPassword} />
  <Route path="/reset-password/:token/" component={ResetPassword} />
  <Route path="/reset-password" component={ResetPassword} />
  <Route path="/reset-password/" component={ResetPassword} />


   
      <Route path="/dashboard">
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      </Route>

      <Route path="/trading">
        <ProtectedRoute>
          <StartTrading />
        </ProtectedRoute>
      </Route>

      <Route path="/start-trading">
        <ProtectedRoute>
          <StartTrading />
        </ProtectedRoute>
      </Route>

      <Route path="/profile">
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      </Route>

      <Route path="/">
        <ConditionalHome />
      </Route>

      {/* 404 */}
      <Route component={NotFound} />
    </Switch>
  );
}


function ConditionalHome() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(226,26%,7%)]">
        <LoadingSpinner />
      </div>
    );
  }

  return isAuthenticated ? <Home /> : <Landing />;
}

function AppInner() {
  const [booting, setBooting] = useState(true);
  const [cookieSettingsOpen, setCookieSettingsOpen] = useState(false);
  useEffect(() => {

    (async () => {
      await bootstrapAuth();
      setBooting(false);
    })();
  }, []);

  useEffect(() => {
    const handler = () => setCookieSettingsOpen(true);
    window.addEventListener('open-cookie-settings', handler as any);
    return () => window.removeEventListener('open-cookie-settings', handler as any);
  }, []);

  const { consent } = useCookieConsent();
  useEffect(() => {
   
    initConsentEffects(() => consent || null);
  }, [consent]);


  function HeartbeatBridge() {
    const { isAuthenticated } = useAuth();
    useHeartbeat({ enabled: !!isAuthenticated, intervalMs: 60_000, jitterMs: 10_000 });
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <div className="dark">
          <HeartbeatBridge />
          <Toaster />
          <ScrollToTop />
          
          {consent?.analytics ? <AnalyticsTracker /> : null}
          <CookieBanner />
       
          <CookieSettings
            open={cookieSettingsOpen}
            onClose={() => {
              setCookieSettingsOpen(false);
              try { window.dispatchEvent(new Event('cookie-settings-closed')); } catch {}
            }}
          />
          {booting ? (
            <div className="min-h-screen flex items-center justify-center bg-[hsl(226,26%,7%)]">
              <LoadingSpinner />
            </div>
          ) : (
            <Router />
          )}
        </div>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

function App() {
  return (
    <CookieConsentProvider>
      <AppInner />
    </CookieConsentProvider>
  );
}

export default App;